import React from 'react';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Download,
  Gift,
  Loader2,
  Phone,
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import TombolaModal from './TombolaModal';

const ResultsPreviewScreen = ({
  language,
  userInfo,
  setUserInfo,
  answers,
  naAnswers,
  industries,
  companySizes,
  submitting,
  error,
  submitAssessment,
  setShowContactForm,
  setShowTombola,
  showTombola,
  tombolaCompleted,
  setTombolaCompleted,
  sessionId,
  totalDimensions,
  setCurrentDimension,
}) => {
  // Calculate preview score
  const naQuestions = Object.keys(naAnswers).length;
  const numericAnswers = Object.entries(answers).filter(([, val]) => typeof val === 'number');
  const answeredQuestions = numericAnswers.length;
  const totalAnswered = answeredQuestions + naQuestions;
  const totalScore = numericAnswers.reduce((sum, [, val]) => sum + (val || 0), 0);
  const avgScore = answeredQuestions > 0 ? (totalScore / answeredQuestions) : 0;
  const previewPercentage = Math.round((avgScore / 6) * 100);
  const canSubmit = userInfo.name && userInfo.email && userInfo.companyName && userInfo.jobTitle && userInfo.industry && userInfo.companySize;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="py-8">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Success indicator */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {language === 'nl' ? 'Uitstekend! Alle vragen beantwoord!' : 'Excellent! All questions answered!'}
            </h1>
            <p className="text-gray-600">
              {language === 'nl' 
                ? `U hebt ${totalAnswered} vragen beantwoord. Uw resultaten zijn klaar.`
                : `You answered ${totalAnswered} questions. Your results are ready.`}
            </p>
          </div>

          {/* Score Preview Teaser */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl p-6 mb-8 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12" />
        
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-blue-100 text-sm mb-1">
                    {language === 'nl' ? 'Uw voorlopige score' : 'Your preliminary score'}
                  </p>
                  <div className="text-5xl font-bold">{previewPercentage}%</div>
                </div>
                <div className="text-right">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
                    <p className="text-xs text-blue-100">
                      {language === 'nl' ? 'Benchmark positie' : 'Benchmark position'}
                    </p>
                    <p className="text-lg font-bold flex items-center gap-1">
                      <span className="blur-sm">Top 25%</span>
                      <span className="text-xs">🔒</span>
                    </p>
                  </div>
                </div>
              </div>
          
              <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                <p className="text-sm text-blue-100 mb-2">
                  {language === 'nl' ? 'Uw volledige rapport bevat:' : 'Your full report includes:'}
                </p>
                <ul className="text-sm space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    {language === 'nl' ? 'Gedetailleerde score per categorie' : 'Detailed score per category'}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    {language === 'nl' ? 'Vergelijking met sector benchmark' : 'Comparison with industry benchmark'}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    {language === 'nl' ? 'Gepersonaliseerde aanbevelingen' : 'Personalized recommendations'}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    {language === 'nl' ? 'Download als PDF rapport' : 'Download as PDF report'}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Contact Form Card */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {language === 'nl' 
                  ? 'Vul uw gegevens in om uw benchmarkrapport te ontvangen'
                  : 'Enter your details to receive your benchmark report'}
              </h2>
              <p className="text-gray-600 text-sm">
                {language === 'nl'
                  ? 'We sturen het rapport naar uw e-mailadres en bewaren uw resultaten zodat u ze later kunt raadplegen.'
                  : 'We\'ll send the report to your email and save your results so you can access them later.'}
              </p>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'nl' ? 'Naam' : 'Name'} *
                  </label>
                  <Input
                    value={userInfo.name}
                    onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
                    placeholder={language === 'nl' ? 'Uw naam' : 'Your name'}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <Input
                    type="email"
                    value={userInfo.email}
                    onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
                    placeholder="email@company.com"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'nl' ? 'Bedrijf' : 'Company'} *
                  </label>
                  <Input
                    value={userInfo.companyName}
                    onChange={(e) => setUserInfo({ ...userInfo, companyName: e.target.value })}
                    placeholder={language === 'nl' ? 'Bedrijfsnaam' : 'Company name'}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'nl' ? 'Functie' : 'Job Title'} *
                  </label>
                  <Input
                    value={userInfo.jobTitle}
                    onChange={(e) => setUserInfo({ ...userInfo, jobTitle: e.target.value })}
                    placeholder="HR Director, CHRO..."
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'nl' ? 'Sector' : 'Industry'} *
                  </label>
                  <Select
                    value={userInfo.industry}
                    onValueChange={(value) => setUserInfo({ ...userInfo, industry: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={language === 'nl' ? 'Selecteer sector' : 'Select industry'} />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((ind) => (
                        <SelectItem key={ind.value} value={ind.value}>
                          {ind.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'nl' ? 'Bedrijfsgrootte' : 'Company Size'} *
                  </label>
                  <Select
                    value={userInfo.companySize}
                    onValueChange={(value) => setUserInfo({ ...userInfo, companySize: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={language === 'nl' ? 'Aantal medewerkers' : 'Number of employees'} />
                    </SelectTrigger>
                    <SelectContent>
                      {companySizes.map((size) => (
                        <SelectItem key={size.value} value={size.value}>
                          {size.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'nl' ? 'Telefoonnummer' : 'Phone'} ({language === 'nl' ? 'optioneel' : 'optional'})
                </label>
                <Input
                  type="tel"
                  value={userInfo.phone}
                  onChange={(e) => setUserInfo({ ...userInfo, phone: e.target.value })}
                  placeholder="+32 xxx xx xx xx"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-lg">{error}</p>
              )}

              {/* Tombola CTA - show after form is filled but before submit */}
              {canSubmit && !tombolaCompleted && (
                <div className="bg-gradient-to-r from-amber-50 to-blue-50 rounded-xl p-4 border-2 border-amber-200 mt-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0 animate-pulse">
                      <Gift className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-amber-900">
                        {language === 'nl' ? '🎉 Bonus: Speel de Tombola!' : '🎉 Bonus: Play the Tombola!'}
                      </h4>
                      <p className="text-sm text-amber-700">
                        {language === 'nl' 
                          ? 'Win direct prijzen zoals boeken, tickets en workshops!'
                          : 'Win instant prizes like books, tickets and workshops!'}
                      </p>
                    </div>
                    <Button
                      onClick={() => setShowTombola(true)}
                      className="bg-gradient-to-r from-blue-500 to-amber-500 hover:from-blue-600 hover:to-amber-600 text-white font-bold"
                    >
                      <Gift className="w-4 h-4 mr-2" />
                      {language === 'nl' ? 'Speel Nu' : 'Play Now'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Show completed tombola status */}
              {tombolaCompleted && (
                <div className="bg-green-50 rounded-xl p-4 border border-green-200 mt-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <div>
                      <p className="font-medium text-green-800">
                        {language === 'nl' ? 'Tombola gespeeld!' : 'Tombola played!'}
                      </p>
                      <p className="text-sm text-green-600">
                        {language === 'nl' 
                          ? 'Verstuur nu je gegevens om je rapport te ontvangen.'
                          : 'Submit your details now to receive your report.'}
                      </p>
                    </div>
                    <Button
                      onClick={() => setShowTombola(true)}
                      variant="outline"
                      size="sm"
                      className="ml-auto border-green-300 text-green-700 hover:bg-green-100"
                    >
                      {language === 'nl' ? 'Opnieuw Spelen' : 'Play Again'}
                    </Button>
                  </div>
                </div>
              )}

              <Button
                onClick={submitAssessment}
                disabled={!canSubmit || submitting}
                className="w-full bg-blue-500 hover:bg-blue-600 py-6 text-lg mt-4"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {language === 'nl' ? 'Rapport wordt gegenereerd...' : 'Generating report...'}
                  </>
                ) : (
                  <>
                    {language === 'nl' ? 'Ontvang Mijn Benchmarkrapport' : 'Get My Benchmark Report'}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center mt-4">
                {language === 'nl'
                  ? 'Door te versturen gaat u akkoord met ons privacybeleid. We delen uw gegevens nooit met derden.'
                  : 'By submitting, you agree to our privacy policy. We never share your data with third parties.'}
              </p>
            </div>
          </div>

          {/* Tombola Modal */}
          <TombolaModal
            isOpen={showTombola}
            onClose={() => {
              setShowTombola(false);
              setTombolaCompleted(true);
            }}
            userInfo={{
              name: userInfo.name,
              email: userInfo.email,
              company: userInfo.companyName,
              companySize: userInfo.companySize,
              jobTitle: userInfo.jobTitle
            }}
            assessmentId={sessionId || 'quickscan-session'}
          />

          {/* Back button */}
          <div className="text-center mt-6">
            <button
              onClick={() => {
                setShowContactForm(false);
                setCurrentDimension(totalDimensions - 1);
              }}
              className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1 mx-auto"
            >
              <ArrowLeft className="h-4 w-4" />
              {language === 'nl' ? 'Terug naar vragen' : 'Back to questions'}
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ResultsPreviewScreen;
